/**
 * Hunts tab — DSL query editor + saved hunts + starter catalog.
 * Spec: docs/lovable specs → LOVABLE_HUNTS_PAGE.md
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { toast } from "sonner";
import { Check, Loader2, MoreHorizontal, Play, Save, Search, Trash2, X } from "lucide-react";

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

import { Panel } from "@/components/surface/Panel";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { OwnerPill } from "@/components/owner-pill";

import {
  useDeleteHunt,
  useHunts,
  useHuntCount,
  useReplaySigmaRule,
  useRunHunt,
  useSaveHunt,
  useStarterHunts,
  useSubmitAdhocReplay,
  useValidateHunt,
} from "@/hooks/security/useHunts";
import type { HuntDef, HuntEvent } from "@/types/edrHunts";
import { STARTER_HUNTS, type StarterHunt } from "./starterHunts";

interface HuntsTabProps {
  initialQuery: string;
  initialWindow: WindowKey;
  initialHuntId?: string;
  onChange: (next: { q?: string; since?: WindowKey; hunt?: string | null }) => void;
  onReplayQueued: (jobId: string) => void;
}

export type WindowKey = "1h" | "24h" | "7d" | "30d";
const WINDOW_HOURS: Record<WindowKey, number> = { "1h": 1, "24h": 24, "7d": 168, "30d": 720 };
const WINDOW_LABEL: Record<WindowKey, string> = {
  "1h": "Last 1h",
  "24h": "Last 24h",
  "7d": "Last 7d",
  "30d": "Last 30d",
};

export function HuntsTab({
  initialQuery,
  initialWindow,
  initialHuntId,
  onChange,
  onReplayQueued,
}: HuntsTabProps) {
  const [queryDsl, setQueryDsl] = useState(initialQuery);
  const [windowKey, setWindowKey] = useState<WindowKey>(initialWindow);
  const [validation, setValidation] = useState<
    { state: "idle" | "ok"; message?: string; position?: number } | { state: "error"; message: string; position?: number }
  >({ state: "idle" });
  const [results, setResults] = useState<{
    events: HuntEvent[];
    matchCount?: number;
    truncated?: boolean;
    elapsedMs?: number;
  } | null>(null);
  const [saveOpen, setSaveOpen] = useState(false);
  const [editingHunt, setEditingHunt] = useState<HuntDef | null>(null);
  const [sigmaOpen, setSigmaOpen] = useState(false);
  const [eventDetail, setEventDetail] = useState<HuntEvent | null>(null);

  const huntsQ = useHunts();
  const validateM = useValidateHunt();
  const runM = useRunHunt();
  const saveM = useSaveHunt();
  const replayM = useSubmitAdhocReplay();
  const sigmaReplayM = useReplaySigmaRule();

  // Push query + window + hunt to URL (debounced for query).
  useEffect(() => {
    const id = setTimeout(() => onChange({ q: queryDsl || undefined }), 500);
    return () => clearTimeout(id);
  }, [queryDsl, onChange]);

  useEffect(() => {
    onChange({ since: windowKey });
  }, [windowKey, onChange]);

  // Debounced validate-as-you-type.
  const lastValidated = useRef<string>("");
  useEffect(() => {
    const trimmed = queryDsl.trim();
    if (!trimmed) {
      setValidation({ state: "idle" });
      return;
    }
    const id = setTimeout(() => {
      if (lastValidated.current === trimmed) return;
      lastValidated.current = trimmed;
      validateM.mutate(trimmed, {
        onSuccess: (r) => {
          if (r.valid) {
            setValidation({ state: "ok", message: "Valid" });
          } else {
            setValidation({
              state: "error",
              message: r.error ?? "Invalid query",
              position: r.position,
            });
          }
        },
        onError: () => setValidation({ state: "idle" }),
      });
    }, 500);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryDsl]);

  // Preload a saved hunt when `?hunt=` is set on first mount.
  useEffect(() => {
    if (!initialHuntId || !huntsQ.data) return;
    const found = huntsQ.data.find((h) => h.id === initialHuntId);
    if (found) loadHunt(found, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialHuntId, huntsQ.data]);

  function loadHunt(h: HuntDef, autoRun: boolean) {
    setQueryDsl(h.queryDsl);
    if (h.defaultWindowHours) {
      const wk = (Object.keys(WINDOW_HOURS) as WindowKey[]).find(
        (k) => WINDOW_HOURS[k] === h.defaultWindowHours,
      );
      if (wk) setWindowKey(wk);
    }
    onChange({ hunt: h.id });
    if (autoRun) {
      setTimeout(() => runQuery(), 50);
    }
  }

  function runQuery() {
    const dsl = queryDsl.trim();
    if (!dsl) {
      toast.error("Type a query first");
      return;
    }
    runM.mutate(
      { queryDsl: dsl, windowHours: WINDOW_HOURS[windowKey], limit: 500 },
      {
        onSuccess: (r) =>
          setResults({
            events: r.events ?? [],
            matchCount: r.matchCount,
            truncated: r.truncated,
            elapsedMs: r.elapsedMs,
          }),
        onError: (e: any) => toast.error(e?.message ?? "Search failed"),
      },
    );
  }

  function submitReplay(wKey: WindowKey) {
    const dsl = queryDsl.trim();
    if (!dsl) {
      toast.error("Type a query first");
      return;
    }
    replayM.mutate(
      { queryDsl: dsl, windowHours: WINDOW_HOURS[wKey] },
      {
        onSuccess: (r) => {
          toast.success("Replay job queued");
          if (r.id) onReplayQueued(r.id);
        },
        onError: (e: any) => toast.error(e?.message ?? "Failed to queue replay"),
      },
    );
  }

  const hunts = huntsQ.data ?? [];
  const noHunts = !huntsQ.isLoading && hunts.length === 0;

  return (
    <div className="space-y-4">
      {/* ── EDITOR ────────────────────────────────────────────────── */}
      <Panel
        title="Query"
        actions={
          <div className="flex items-center gap-2">
            <Select value={windowKey} onValueChange={(v) => setWindowKey(v as WindowKey)}>
              <SelectTrigger className="h-8 w-[120px] text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(WINDOW_LABEL) as WindowKey[]).map((k) => (
                  <SelectItem key={k} value={k}>
                    {WINDOW_LABEL[k]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              size="sm"
              variant="outline"
              className="h-8"
              onClick={() => runQuery()}
              disabled={runM.isPending}
            >
              {runM.isPending ? (
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              ) : (
                <Play className="h-3.5 w-3.5 mr-1.5" />
              )}
              Run
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="outline" className="h-8">
                  Replay ▾
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => submitReplay("24h")}>
                  Replay against last 24h
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => submitReplay("7d")}>
                  Replay against last 7d
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => submitReplay("30d")}>
                  Replay against last 30d
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setSigmaOpen(true)}>
                  Replay a Sigma rule…
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              size="sm"
              variant="default"
              className="h-8"
              onClick={() => {
                setEditingHunt(null);
                setSaveOpen(true);
              }}
              disabled={!queryDsl.trim()}
            >
              <Save className="h-3.5 w-3.5 mr-1.5" />
              Save
            </Button>
          </div>
        }
      >
        <div className="space-y-2">
          <Textarea
            value={queryDsl}
            onChange={(e) => setQueryDsl(e.target.value)}
            placeholder="event_type:exec AND process.path:*powershell*"
            className="font-mono text-sm min-h-[88px] resize-y"
            spellCheck={false}
          />
          <ValidationIndicator state={validation} pending={validateM.isPending} />
          <SyntaxHelp />
        </div>
      </Panel>

      {/* ── RESULTS ───────────────────────────────────────────────── */}
      <ResultsPanel
        results={results}
        loading={runM.isPending}
        onReplay={() => submitReplay(windowKey)}
        onSaveAsHunt={() => {
          setEditingHunt(null);
          setSaveOpen(true);
        }}
        onOpenEvent={(e) => setEventDetail(e)}
      />

      {/* ── STARTER CATALOG (when no saved hunts) ─────────────────── */}
      {noHunts && (
        <StarterCatalog
          onPick={(s) => {
            setQueryDsl(s.queryDsl);
            toast.success(`Loaded: ${s.name}`);
          }}
        />
      )}

      {/* ── SAVED HUNTS ───────────────────────────────────────────── */}
      <SavedHuntsPanel
        loading={huntsQ.isLoading}
        isError={huntsQ.isError}
        error={huntsQ.error}
        onRetry={() => huntsQ.refetch()}
        hunts={hunts}
        onRun={(h) => loadHunt(h, true)}
        onEdit={(h) => {
          setEditingHunt(h);
          setQueryDsl(h.queryDsl);
          setSaveOpen(true);
        }}
        onReplay={(h, key) =>
          replayM.mutate(
            { queryDsl: h.queryDsl, windowHours: WINDOW_HOURS[key] },
            {
              onSuccess: (r) => {
                toast.success(`Replay queued: ${h.name}`);
                if (r.id) onReplayQueued(r.id);
              },
              onError: (e: any) => toast.error(e?.message ?? "Failed to queue replay"),
            },
          )
        }
      />

      {/* ── SAVE DIALOG ───────────────────────────────────────────── */}
      <SaveHuntDialog
        open={saveOpen}
        onOpenChange={setSaveOpen}
        initial={editingHunt}
        queryDsl={queryDsl}
        defaultWindowHours={WINDOW_HOURS[windowKey]}
        onSave={(payload) =>
          saveM.mutate(payload, {
            onSuccess: () => {
              toast.success(editingHunt ? "Hunt updated" : "Hunt saved");
              setSaveOpen(false);
            },
            onError: (e: any) => toast.error(e?.message ?? "Failed to save"),
          })
        }
        saving={saveM.isPending}
      />

      <EventDetailDrawer event={eventDetail} onClose={() => setEventDetail(null)} />
      <SigmaReplayDialog
        open={sigmaOpen}
        onOpenChange={setSigmaOpen}
        onReplayed={(jobId) => onReplayQueued(jobId)}
      />
    </div>
  );
}

/* ───────────────────────── Validation indicator ───────────────────────── */

function ValidationIndicator({
  state,
  pending,
}: {
  state: { state: "idle" | "ok" | "error"; message?: string; position?: number };
  pending: boolean;
}) {
  if (state.state === "idle" && !pending) return null;
  if (pending) {
    return (
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Loader2 className="h-3 w-3 animate-spin" /> Validating…
      </div>
    );
  }
  if (state.state === "ok") {
    return (
      <div className="flex items-center gap-1.5 text-xs text-success">
        <Check className="h-3 w-3" /> Valid query
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1.5 text-xs text-destructive">
      <X className="h-3 w-3" />
      {state.position != null ? `Position ${state.position}: ` : ""}
      {state.message}
    </div>
  );
}

function SyntaxHelp() {
  return (
    <details className="text-[11px] text-muted-foreground">
      <summary className="cursor-pointer hover:text-foreground">Syntax reference</summary>
      <div className="mt-2 space-y-1 rounded-md border border-border bg-muted/30 p-2 font-mono leading-relaxed">
        <div><span className="text-foreground">event_type</span>: exec · file · net · auth · attestation</div>
        <div><span className="text-foreground">platform</span>: macos · windows · linux · android · chromeos</div>
        <div><span className="text-foreground">process.path</span> · <span className="text-foreground">parent.process.path</span> · <span className="text-foreground">process.team_id</span> · <span className="text-foreground">process.signing_id</span></div>
        <div><span className="text-foreground">raw_payload.*</span> — JSON path into raw payload</div>
        <div className="pt-1">Operators: AND · OR · NOT · ( ) · :*wildcard* · :[a b c] · :&gt;N · :&lt;N</div>
      </div>
    </details>
  );
}

/* ───────────────────────── Results panel ───────────────────────── */

function ResultsPanel({
  results,
  loading,
  onReplay,
  onSaveAsHunt,
  onOpenEvent,
}: {
  results: { events: HuntEvent[]; matchCount?: number; truncated?: boolean; elapsedMs?: number } | null;
  loading: boolean;
  onReplay: () => void;
  onSaveAsHunt: () => void;
  onOpenEvent: (e: HuntEvent) => void;
}) {
  if (loading) {
    return (
      <Panel title="Results" loading skeleton="table">
        <div />
      </Panel>
    );
  }
  if (!results) {
    return (
      <Panel title="Results">
        <div className="py-8 text-center text-xs text-muted-foreground space-y-2">
          <p>Type a query above and click Run to search the last 24 hours.</p>
          <ul className="space-y-1">
            <li><code className="font-mono">event_type:exec</code> finds every process launch</li>
            <li>Use <code className="font-mono">*wildcard*</code> for partial process paths</li>
            <li>Combine with AND / OR / NOT and parentheses</li>
          </ul>
        </div>
      </Panel>
    );
  }
  const { events, matchCount, truncated, elapsedMs } = results;
  return (
    <Panel
      title={`Results · ${matchCount ?? events.length} matches${
        elapsedMs ? ` in ${(elapsedMs / 1000).toFixed(1)}s` : ""
      }`}
      actions={
        events.length > 0 && (
          <Button size="sm" variant="outline" className="h-7" onClick={onSaveAsHunt}>
            <Save className="h-3 w-3 mr-1" /> Save as hunt
          </Button>
        )
      }
    >
      {truncated && (
        <div className="mb-3 rounded-md border border-warning/40 bg-warning-muted/30 px-3 py-2 text-xs text-warning-foreground">
          ⚠ Showing first {events.length} of {matchCount} matches. For the full set,{" "}
          <button onClick={onReplay} className="underline font-medium">
            replay against history
          </button>
          .
        </div>
      )}
      {events.length === 0 ? (
        <div className="py-6 text-center text-xs text-muted-foreground">No matches.</div>
      ) : (
        <HuntsResultsTable events={events} onOpen={onOpenEvent} />
      )}
    </Panel>
  );
}

function eventTypeColor(t?: string): string {
  switch (t) {
    case "exec": return "bg-info/10 text-info border-info/30";
    case "file": return "bg-accent text-accent-foreground border-border";
    case "net": return "bg-warning/10 text-warning border-warning/30";
    case "auth": return "bg-success/10 text-success border-success/30";
    default: return "bg-muted text-muted-foreground border-border";
  }
}

function platformIcon(p?: string): string {
  switch (p) {
    case "macos": return "";
    case "windows": return "⊞";
    case "linux": return "🐧";
    case "android": return "🤖";
    case "chromeos": return "◐";
    default: return "";
  }
}

function HuntsResultsTable({ events, onOpen }: { events: HuntEvent[]; onOpen: (e: HuntEvent) => void }) {
  return (
    <div className="overflow-x-auto -mx-4 -my-4">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-border bg-muted/30 text-[10px] uppercase tracking-wider text-muted-foreground">
            <th className="px-3 py-2 text-left">Time</th>
            <th className="px-3 py-2 text-left">Device</th>
            <th className="px-3 py-2 text-left">Event</th>
            <th className="px-3 py-2 text-left">Process</th>
            <th className="px-3 py-2 text-left w-[60px]">PID</th>
            <th className="px-3 py-2 text-left">Parent</th>
            <th className="px-3 py-2 text-left">Owner</th>
          </tr>
        </thead>
        <tbody>
          {events.slice(0, 500).map((e, i) => (
            <tr
              key={e.id ?? i}
              className="border-b border-border/60 hover:bg-muted/40 cursor-pointer"
              onClick={() => onOpen(e)}
            >
              <td className="px-3 py-2 text-muted-foreground tabular-nums" title={e.receivedAt}>
                {e.receivedAt
                  ? formatDistanceToNow(new Date(e.receivedAt), { addSuffix: true })
                  : "—"}
              </td>
              <td className="px-3 py-2 font-mono text-foreground truncate max-w-[180px]">
                {platformIcon(e.platform) && (
                  <span className="mr-1 text-muted-foreground">{platformIcon(e.platform)}</span>
                )}
                {e.hostname ?? e.deviceId ?? "—"}
              </td>
              <td className="px-3 py-2">
                <span
                  className={cn(
                    "rounded border px-1.5 py-0.5 text-[10px] uppercase tracking-wider",
                    eventTypeColor(e.eventType),
                  )}
                >
                  {e.eventType ?? "—"}
                </span>
              </td>
              <td className="px-3 py-2 font-mono text-foreground truncate max-w-[260px]" title={e.process?.path}>
                {e.process?.path ?? "—"}
              </td>
              <td className="px-3 py-2 font-mono text-muted-foreground tabular-nums">
                {e.process?.pid ?? "—"}
              </td>
              <td className="px-3 py-2 font-mono text-muted-foreground truncate max-w-[200px]" title={e.process?.parentPath}>
                {e.process?.parentPath ?? "—"}
              </td>
              <td className="px-3 py-2">
                {e.process?.consoleUser ? (
                  <OwnerPill consoleUser={e.process.consoleUser} deviceId={e.deviceId} size="sm" />
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ───────────────────────── Event detail drawer ───────────────────────── */

function EventDetailDrawer({ event, onClose }: { event: HuntEvent | null; onClose: () => void }) {
  return (
    <Sheet open={!!event} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto">
        {event && (
          <>
            <SheetHeader>
              <SheetTitle className="flex items-center gap-2">
                Event detail
                {event.eventType && (
                  <span
                    className={cn(
                      "rounded border px-1.5 py-0.5 text-[10px] uppercase tracking-wider",
                      eventTypeColor(event.eventType),
                    )}
                  >
                    {event.eventType}
                  </span>
                )}
              </SheetTitle>
              <SheetDescription>
                {event.hostname ?? event.deviceId ?? "—"} ·{" "}
                {event.receivedAt
                  ? formatDistanceToNow(new Date(event.receivedAt), { addSuffix: true })
                  : "—"}
              </SheetDescription>
            </SheetHeader>
            <div className="mt-4 space-y-3 text-xs">
              <div className="grid grid-cols-[100px_1fr] gap-2">
                <span className="text-muted-foreground">Process</span>
                <code className="font-mono break-all">{event.process?.path ?? "—"}</code>
                <span className="text-muted-foreground">PID</span>
                <code className="font-mono">{event.process?.pid ?? "—"}</code>
                <span className="text-muted-foreground">Parent</span>
                <code className="font-mono break-all">{event.process?.parentPath ?? "—"}</code>
                <span className="text-muted-foreground">Team ID</span>
                <code className="font-mono">{event.process?.teamId ?? "—"}</code>
                <span className="text-muted-foreground">Signing ID</span>
                <code className="font-mono break-all">{event.process?.signingId ?? "—"}</code>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
                  Raw payload
                </div>
                <pre className="rounded-md bg-muted/40 p-2 font-mono text-[10px] overflow-x-auto max-h-[60vh]">
                  {JSON.stringify(event.rawPayload ?? event, null, 2)}
                </pre>
              </div>
              {event.deviceId && (
                <div className="pt-2 border-t border-border">
                  <Link
                    to={`/security/investigate?tab=timeline&deviceId=${event.deviceId}`}
                    className="text-xs underline text-foreground hover:text-primary"
                  >
                    Open device timeline →
                  </Link>
                </div>
              )}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

/* ───────────────────────── Sigma rule replay dialog ───────────────────────── */

function SigmaReplayDialog({
  open,
  onOpenChange,
  onReplayed,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onReplayed: (jobId: string) => void;
}) {
  const [ruleId, setRuleId] = useState("");
  const [windowH, setWindowH] = useState("168");
  const m = useReplaySigmaRule();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Replay a Sigma rule against history</DialogTitle>
          <DialogDescription>
            Submits a Sigma rule as a background replay job against the chosen window.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Sigma rule ID</Label>
            <Input
              value={ruleId}
              onChange={(e) => setRuleId(e.target.value)}
              placeholder="sigma_powershell_suspicious"
              className="h-8 text-xs font-mono"
            />
          </div>
          <div>
            <Label className="text-xs">Window</Label>
            <Select value={windowH} onValueChange={setWindowH}>
              <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="24">Last 24h</SelectItem>
                <SelectItem value="168">Last 7d</SelectItem>
                <SelectItem value="720">Last 30d</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            disabled={!ruleId.trim() || m.isPending}
            onClick={() =>
              m.mutate(
                { id: ruleId.trim(), windowHours: Number(windowH) },
                {
                  onSuccess: (r) => {
                    toast.success("Sigma replay queued");
                    onOpenChange(false);
                    setRuleId("");
                    if (r.id) onReplayed(r.id);
                  },
                  onError: (e: any) => toast.error(e?.message ?? "Failed to queue replay"),
                },
              )
            }
          >
            {m.isPending ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : null}
            Replay
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* ───────────────────────── Saved hunts panel ───────────────────────── */

function SavedHuntsPanel({
  loading,
  isError,
  error,
  onRetry,
  hunts,
  onRun,
  onEdit,
  onReplay,
}: {
  loading: boolean;
  isError: boolean;
  error: unknown;
  onRetry: () => void;
  hunts: HuntDef[];
  onRun: (h: HuntDef) => void;
  onEdit: (h: HuntDef) => void;
  onReplay: (h: HuntDef, key: WindowKey) => void;
}) {
  const deleteM = useDeleteHunt();

  return (
    <Panel
      title={`Saved hunts · ${hunts.length}`}
      loading={loading}
      isError={isError}
      error={error}
      onRetry={onRetry}
      empty={!loading && !isError && hunts.length === 0}
      emptyTitle="No hunts saved yet"
      emptyDescription="Pick a starter pattern above or write your own DSL in the editor. Hunts are reusable searches — perfect for tracking a hypothesis across investigations."
      skeleton="list"
    >
      <ul className="-mx-4 -my-4 divide-y divide-border">
        {hunts.map((h) => (
          <li key={h.id} className="px-4 py-3 flex items-center gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-medium text-foreground truncate">{h.name}</span>
                <LazyHitBadge hunt={h} />
                {h.tags?.map((t) => (
                  <Badge key={t} variant="outline" className="text-[10px] font-normal text-muted-foreground">
                    #{t}
                  </Badge>
                ))}
                {h.updatedAt && (
                  <span className="text-[10px] text-muted-foreground" title={h.updatedAt}>
                    Updated {formatDistanceToNow(new Date(h.updatedAt), { addSuffix: true })}
                  </span>
                )}
              </div>
              {h.description && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <p className="mt-0.5 text-[11px] text-muted-foreground truncate">{h.description}</p>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-sm">{h.description}</TooltipContent>
                </Tooltip>
              )}
              <code className="mt-1 block text-[10px] font-mono text-muted-foreground/80 truncate" title={h.queryDsl}>
                {h.queryDsl}
              </code>
            </div>
            <div className="flex items-center gap-1 flex-none">
              <Button size="sm" variant="outline" className="h-7" onClick={() => onRun(h)}>
                <Play className="h-3 w-3 mr-1" /> Run
              </Button>
              <Button size="sm" variant="ghost" className="h-7" onClick={() => onEdit(h)}>
                Edit
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0">
                    <MoreHorizontal className="h-3.5 w-3.5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => onReplay(h, "7d")}>
                    Replay against 7d
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onReplay(h, "30d")}>
                    Replay against 30d
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => {
                      navigator.clipboard.writeText(h.queryDsl);
                      toast.success("DSL copied");
                    }}
                  >
                    Copy DSL
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => {
                      const url = `${window.location.origin}/investigate?tab=hunts&hunt=${h.id}`;
                      navigator.clipboard.writeText(url);
                      toast.success("Share link copied");
                    }}
                  >
                    Copy share link
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="text-destructive"
                    onClick={() => {
                      if (!confirm(`Delete "${h.name}"?`)) return;
                      deleteM.mutate(h.id, {
                        onSuccess: () => toast.success("Hunt deleted"),
                        onError: (e: any) => toast.error(e?.message ?? "Failed to delete"),
                      });
                    }}
                  >
                    <Trash2 className="h-3 w-3 mr-1.5" /> Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </li>
        ))}
      </ul>
    </Panel>
  );
}

function LazyHitBadge({ hunt }: { hunt: HuntDef }) {
  const w = hunt.defaultWindowHours ?? 24;
  const countQ = useHuntCount(hunt.id, hunt.queryDsl, w);
  if (countQ.isPending) {
    return <Badge variant="outline" className="text-[10px] font-normal">…</Badge>;
  }
  if (countQ.isError) return null;
  const n = countQ.data ?? 0;
  return (
    <Badge variant={n > 0 ? "default" : "outline"} className="text-[10px] font-normal">
      {n} hits · {w >= 24 ? `${w / 24}d` : `${w}h`}
    </Badge>
  );
}

/* ───────────────────────── Starter catalog ───────────────────────── */

function StarterCatalog({ onPick }: { onPick: (s: StarterHunt) => void }) {
  const starterQ = useStarterHunts(STARTER_HUNTS);
  const hunts = starterQ.data ?? STARTER_HUNTS;
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? hunts : hunts.slice(0, 5);
  return (
    <Panel
      title="Starter hunts"
      subtitle="Try one of these to see how Hunts works. Each is a known threat-hunting pattern — click to load, then Run."
    >
      <ul className="-mx-4 -my-4 divide-y divide-border">
        {visible.map((s) => (
          <li key={s.id} className="px-4 py-3">
            <button
              onClick={() => onPick(s)}
              className="w-full text-left rounded-md p-2 hover:bg-muted/40 transition-colors"
            >
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-base">{s.icon ?? "🔍"}</span>
                <span className="text-sm font-medium text-foreground">{s.name}</span>
                {s.mitreTactic && (
                  <Badge variant="outline" className="text-[10px] font-normal">
                    {s.mitreTactic}
                    {s.mitreTechId ? ` · ${s.mitreTechId}` : ""}
                  </Badge>
                )}
                {s.platforms?.map((p) => (
                  <Badge key={p} variant="outline" className="text-[10px] font-normal text-muted-foreground">
                    {p}
                  </Badge>
                ))}
              </div>
              <p className="mt-1 text-[11px] text-muted-foreground">{s.description}</p>
              <code className="mt-1 block text-[10px] font-mono text-muted-foreground/80 truncate">
                {s.queryDsl}
              </code>
            </button>
          </li>
        ))}
      </ul>
      {hunts.length > 5 && (
        <div className="px-4 pt-3">
          <Button
            size="sm"
            variant="ghost"
            className="h-7 text-xs"
            onClick={() => setShowAll((v) => !v)}
          >
            {showAll ? "Show fewer" : `Browse all ${hunts.length} starter hunts →`}
          </Button>
        </div>
      )}
    </Panel>
  );
}

/* ───────────────────────── Save hunt dialog ───────────────────────── */

function SaveHuntDialog({
  open,
  onOpenChange,
  initial,
  queryDsl,
  defaultWindowHours,
  onSave,
  saving,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  initial: HuntDef | null;
  queryDsl: string;
  defaultWindowHours: number;
  onSave: (payload: Partial<HuntDef> & { name: string; queryDsl: string }) => void;
  saving: boolean;
}) {
  const [name, setName] = useState(initial?.name ?? "");
  const [description, setDescription] = useState(initial?.description ?? "");
  const [windowH, setWindowH] = useState<number>(initial?.defaultWindowHours ?? defaultWindowHours);
  const [tagsCsv, setTagsCsv] = useState((initial?.tags ?? []).join(", "));


  useEffect(() => {
    if (open) {
      setName(initial?.name ?? "");
      setDescription(initial?.description ?? "");
      setWindowH(initial?.defaultWindowHours ?? defaultWindowHours);
      setTagsCsv((initial?.tags ?? []).join(", "));
    }
  }, [open, initial, defaultWindowHours]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{initial ? "Edit hunt" : "Save this query as a hunt"}</DialogTitle>
          <DialogDescription>
            Give the hunt a memorable name. Saved hunts appear in the list below the editor and can be re-run any time.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Name</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="PowerShell sneakery"
              className="h-8 text-xs"
            />
          </div>
          <div>
            <Label className="text-xs">Description</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What hypothesis is this checking?"
              className="text-xs min-h-[56px]"
            />
          </div>
          <div>
            <Label className="text-xs">Default window</Label>
            <Select value={String(windowH)} onValueChange={(v) => setWindowH(Number(v))}>
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">Last 1h</SelectItem>
                <SelectItem value="24">Last 24h</SelectItem>
                <SelectItem value="168">Last 7d</SelectItem>
                <SelectItem value="720">Last 30d</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Tags (comma-separated)</Label>
            <Input
              value={tagsCsv}
              onChange={(e) => setTagsCsv(e.target.value)}
              placeholder="lateral-movement, T1059"
              className="h-8 text-xs"
            />
          </div>
          <div>
            <Label className="text-xs">Query (read-only preview)</Label>
            <code className="block mt-1 text-[11px] font-mono p-2 rounded bg-muted/40 break-all">
              {queryDsl}
            </code>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={!name.trim() || saving}
            onClick={() =>
              onSave({
                ...(initial?.id ? { id: initial.id } : {}),
                name: name.trim(),
                description: description.trim() || undefined,
                queryDsl,
                defaultWindowHours: windowH,
                tags: tagsCsv
                  .split(",")
                  .map((t) => t.trim())
                  .filter(Boolean),
              })
            }
          >
            {saving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : null}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
